import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
  Users,
  BookOpen,
  Calendar,
  DollarSign,
  Clock,
  FileText,
  ChevronRight,
} from "lucide-react";
import api from "../services/api";
import LoadingSpinner from "../components/common/LoadingSpinner";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeCourses: 0,
    attendanceRate: 0,
    totalRevenue: 0,
    pendingPayments: 0,
    completedCourses: 0,
    upcomingCourses: 0,
    certifications: 0,
  });

  const [recentStudents, setRecentStudents] = useState([]);
  const [upcomingCourses, setUpcomingCourses] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const getStudentDate = (student) =>
    student.createdAt || student.registrationDate || student.enrollmentDate;

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [
          studentsRes,
          coursesRes,
          paymentsRes,
          certificatesRes,
          attendanceRes,
        ] = await Promise.all([
          api
            .get("/users?role=student")
            .then((res) => res.data?.data || res.data || [])
            .catch(() => []),

          api
            .get("/courses")
            .then((res) => res.data?.data || res.data || [])
            .catch(() => []),

          api
            .get("/payments")
            .then((res) => res.data?.data || res.data || [])
            .catch(() => []),

          api
            .get("/certifications")
            .then((res) => res.data?.data || res.data || [])
            .catch(() => []),

          api
            .get("/attendance?limit=100")
            .then((res) => res.data?.data || res.data || [])
            .catch(() => []),
        ]);

        // Process Students
        const sortedStudents = [...studentsRes].sort(
          (a, b) => new Date(getStudentDate(b)) - new Date(getStudentDate(a))
        );
        setRecentStudents(sortedStudents.slice(0, 5));

        // Process Courses
        const activeCourses = coursesRes.filter(
          (c) => c.status?.toLowerCase() === "active"
        ).length;

        const studentsMap = new Map(
          studentsRes.map((student) => [student.id, student])
        );

        const coursesMap = new Map(
          coursesRes.map((course) => [course.id, course])
        );

        const upcomingCourses = coursesRes.filter(
          (c) => c.status?.toLowerCase() === "upcoming"
        );

        const completedCourses = coursesRes.filter(
          (c) => c.status?.toLowerCase() === "completed"
        ).length;

        setUpcomingCourses(upcomingCourses.slice(0, 3));

        // Process Payments
        const completedPayments = paymentsRes
          .filter((p) => p.status?.toLowerCase() === "completed")
          .map((payment) => ({
            ...payment,
            studentName: studentsMap.get(payment.studentId)
              ? `${studentsMap.get(payment.studentId).firstName} ${
                  studentsMap.get(payment.studentId).lastName
                }`
              : "Unknown Student",
            courseName:
              coursesMap.get(payment.courseId)?.name || "No course specified",
          }));

        const pendingPayments = paymentsRes.filter(
          (p) => p.status?.toLowerCase() === "pending"
        ).length;

        setRecentPayments(
          [...completedPayments]
            .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate))
            .slice(0, 5)
        );

        // Process Certifications
        const issuedCertificates = certificatesRes.filter(
          (c) => c.status?.toLowerCase() === "issued"
        ).length;

        // Process Attendance
        const attendanceRate =
          attendanceRes.length > 0
            ? Math.round(
                (attendanceRes.filter((r) => r.present).length /
                  attendanceRes.length) *
                  100
              )
            : 0;

        // Update Stats
        setStats({
          totalStudents: studentsRes.length,
          activeCourses,
          attendanceRate,
          totalRevenue: completedPayments.reduce(
            (sum, p) => sum + (p.amount || 0),
            0
          ),
          pendingPayments,
          completedCourses,
          upcomingCourses: upcomingCourses.length,
          certifications: issuedCertificates,
        });
      } catch (error) {
        console.error("Dashboard data error:", error);
        toast.error("Failed to load some dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formattedRevenue = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(stats.totalRevenue),
    [stats.totalRevenue]
  );

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner message="Loading dashboard data..." />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Academy Dashboard</h1>
        <p className="text-gray-600">
          Overview of academy performance and metrics
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Students */}
        <div className="bg-white rounded-lg shadow p-6 flex items-start">
          <div className="p-3 bg-blue-100 rounded-lg mr-4">
            <Users className="text-blue-600" size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Students</p>
            <h3 className="text-2xl font-bold text-gray-800">
              {stats.totalStudents}
            </h3>
          </div>
        </div>

        {/* Active Courses */}
        <div className="bg-white rounded-lg shadow p-6 flex items-start">
          <div className="p-3 bg-green-100 rounded-lg mr-4">
            <BookOpen className="text-green-600" size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Active Courses</p>
            <h3 className="text-2xl font-bold text-gray-800">
              {stats.activeCourses}
            </h3>
          </div>
        </div>

        {/* Attendance Rate */}
        <div className="bg-white rounded-lg shadow p-6 flex items-start">
          <div className="p-3 bg-amber-100 rounded-lg mr-4">
            <Calendar className="text-amber-600" size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Attendance Rate</p>
            <h3 className="text-2xl font-bold text-gray-800">
              {stats.attendanceRate}%
            </h3>
          </div>
        </div>

        {/* Total Revenue */}
        <div className="bg-white rounded-lg shadow p-6 flex items-start">
          <div className="p-3 bg-purple-100 rounded-lg mr-4">
            <DollarSign className="text-purple-600" size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Revenue</p>
            <h3 className="text-2xl font-bold text-gray-800">
              {formattedRevenue}
            </h3>
          </div>
        </div>
      </div>

      {/* Recent Registrations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Users className="mr-2" size={20} />
            Recent Registrations
          </h3>
          {recentStudents.length > 0 ? (
            <ul className="space-y-3">
              {recentStudents.map((student, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded"
                >
                  <div>
                    <p className="font-medium">
                      {student.firstName} {student.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {student.email || "No email provided"}
                    </p>
                  </div>
                  <span className="text-sm text-gray-500">
                    {formatDate(getStudentDate(student))}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-4 text-gray-500">
              No recent registrations found
            </div>
          )}
        </div>

        {/* Recent Payments */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <DollarSign className="mr-2" size={20} />
            Recent Payments
          </h3>
          {recentPayments.length > 0 ? (
            <ul className="space-y-3">
              {recentPayments.map((payment, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded"
                >
                  <div>
                    <p className="font-medium">{payment.studentName}</p>
                    <p className="text-sm text-gray-500">
                      {payment.courseName}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(payment.amount || 0)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(payment.paymentDate)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-4 text-gray-500">
              No recent payments found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
